import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    X, BookOpen, Target, Calculator, Users, BarChart3, Settings2,
    Zap, Shield, Briefcase, Clock, Activity, Check, Scale,
    TrendingUp, Folder, ArrowRight, ChevronDown, ChevronRight,
    AlertTriangle, Info, Lightbulb, HelpCircle, CheckCircle2, Eye, EyeOff,
    PartyPopper, Percent
} from "lucide-react";
import { cn } from "../../utils/cn";

interface HelpPageProps {
    isOpen: boolean;
    onClose: () => void;
    userRole?: string;
}

const Section = ({ title, icon: Icon, children, defaultOpen = false }: {
    title: string;
    icon: React.ElementType;
    children: React.ReactNode;
    defaultOpen?: boolean;
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    return (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="w-full flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-siemens-petrol to-siemens-deep-blue flex items-center justify-center">
                        <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-bold text-slate-900 dark:text-white">{title}</span>
                </div>
                {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="p-5 space-y-4 text-sm text-slate-600 dark:text-slate-300">
                            {children}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const MetricCard = ({ icon: Icon, name, description, range, color }: {
    icon: React.ElementType;
    name: string;
    description: string;
    range: string;
    color: string;
}) => (
    <div className="p-4 bg-white dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow">
        <div className="flex items-start gap-3">
            <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0", color)}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <h4 className="font-bold text-slate-900 dark:text-white">{name}</h4>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{description}</p>
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 rounded text-xs font-mono">
                    Range: {range}
                </div>
            </div>
        </div>
    </div>
);

const RoleCard = ({ role, access, color, active }: { role: string; access: string[]; color: string; active?: boolean }) => (
    <div className={cn(
        "p-4 bg-white dark:bg-slate-800/50 rounded-xl border transition-all duration-300",
        active
            ? "border-siemens-petrol ring-1 ring-siemens-petrol shadow-lg shadow-siemens-petrol/10 scale-[1.02]"
            : "border-slate-200 dark:border-slate-700"
    )}>
        <div className="flex items-center justify-between mb-3">
            <div className={cn("inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold text-white", color)}>
                <Users className="w-3 h-3" /> {role}
            </div>
            {active && <span className="text-[10px] font-bold text-siemens-petrol bg-siemens-petrol/10 px-2 py-0.5 rounded-full">YOUR ROLE</span>}
        </div>
        <ul className="space-y-1">
            {access.map((item, i) => (
                <li key={i} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                    <Check className="w-3 h-3 text-emerald-500" /> {item}
                </li>
            ))}
        </ul>
    </div>
);

export function HelpPage({ isOpen, onClose, userRole }: HelpPageProps) {
    if (!isOpen) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                        onClick={onClose}
                    />
                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        className="fixed inset-4 md:inset-8 lg:inset-12 z-50 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
                    >
                        {/* Header */}
                        <div className="px-6 py-5 bg-gradient-to-r from-siemens-petrol via-siemens-deep-blue to-siemens-petrol bg-[length:200%_100%] animate-gradient flex items-center justify-between flex-shrink-0">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                                    <BookOpen className="w-6 h-6 text-white" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-black text-white">ZPS Innovation</h1>
                                    <p className="text-sm text-white/80">Help & Documentation</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-white/20 transition-colors"
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {/* Quick Intro */}
                            <div className="p-5 bg-gradient-to-r from-siemens-petrol/10 to-siemens-deep-blue/10 rounded-xl border border-siemens-petrol/20">
                                <div className="flex items-start gap-4">
                                    <Lightbulb className="w-8 h-8 text-siemens-petrol flex-shrink-0" />
                                    <div>
                                        <h2 className="font-bold text-slate-900 dark:text-white mb-2">What is ZPS Innovation?</h2>
                                        <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                            ZPS Innovation is a <strong>strategic prioritization tool</strong> for managing Zervice Projects and their related epics.
                                            It helps teams make data-driven decisions about what to work on next using the <strong>WSJF (Weighted Shortest Job First)</strong> framework
                                            from SAFe (Scaled Agile Framework).
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Section: Getting Started */}
                            <Section title="Getting Started" icon={Target} defaultOpen>
                                <div className="space-y-4">
                                    <p>ZPS Innovation organizes work into <strong>Projects</strong> and <strong>Epics</strong>:</p>

                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Folder className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Projects</span>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                High-level initiatives containing multiple epics.
                                                Each project shows a <strong>progress bar</strong> indicating the % of epics completed.
                                            </p>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <BarChart3 className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Epics</span>
                                            </div>
                                            <p className="text-xs text-slate-500">
                                                Individual work items scored using WSJF.
                                                Epics have <strong>progress tracking</strong> (0-100%) and are placed in priority lanes.
                                            </p>
                                        </div>
                                    </div>

                                    <h4 className="font-bold text-slate-900 dark:text-white flex items-center gap-2 pt-2">
                                        <ArrowRight className="w-4 h-4" /> Lanes (Prioritization Buckets)
                                    </h4>
                                    <div className="grid grid-cols-5 gap-2">
                                        {[
                                            { name: "Now", color: "bg-emerald-500", desc: "In active development" },
                                            { name: "Next", color: "bg-amber-500", desc: "Upcoming priority" },
                                            { name: "Later", color: "bg-blue-500", desc: "Future consideration" },
                                            { name: "Unassigned", color: "bg-slate-400", desc: "Needs prioritization" },
                                            { name: "Done", color: "bg-green-600", desc: "Completed work" }
                                        ].map(lane => (
                                            <div key={lane.name} className="text-center">
                                                <div className={cn("h-2 rounded-full mb-2", lane.color)} />
                                                <div className="text-xs font-bold">{lane.name}</div>
                                                <div className="text-[10px] text-slate-400">{lane.desc}</div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <span className="font-bold text-emerald-800 dark:text-emerald-200">Completion Tracking</span>
                                                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                                                    When you move an epic to <strong>Done</strong>, its progress is automatically set to 100% and a completion date is recorded.
                                                    Moving it back resets progress to 0%.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Section>

                            {/* Section: Progress Tracking */}
                            <Section title="Progress Tracking" icon={Percent}>
                                <div className="space-y-4">
                                    <p>Each epic has a <strong>progress slider</strong> (0-100%) to track completion:</p>

                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Percent className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Epic Progress</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li>• Drag the slider to update progress</li>
                                                <li>• 100% progress auto-moves epic to Done</li>
                                                <li>• Moving to Done sets progress to 100%</li>
                                                <li>• Moving from Done resets progress to 0%</li>
                                            </ul>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Folder className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Project Progress</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li>• Shows % of epics in Done lane</li>
                                                <li>• Color-coded: Orange (&lt;50%), Petrol (50-99%), Green (100%)</li>
                                                <li>• Visible on project cards and dashboards</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <PartyPopper className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <span className="font-bold text-blue-800 dark:text-blue-200">Recently Completed</span>
                                                <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                                                    The Dashboard and Executive views show epics completed in the last 30 days,
                                                    celebrating your team's accomplishments!
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Section>

                            {/* Section: Lane Visibility */}
                            <Section title="Lane Visibility Toggles" icon={Eye}>
                                <div className="space-y-4">
                                    <p>Some lanes are hidden by default to reduce clutter. Use toggle buttons to show/hide them:</p>

                                    <div className="grid md:grid-cols-2 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Settings2 className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Kanban View</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Unassigned - hidden by default</li>
                                                <li className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Done - hidden by default</li>
                                                <li>• Click toggle buttons to show/hide</li>
                                                <li>• Badge shows count of hidden items</li>
                                            </ul>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Target className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Roadmap View</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li className="flex items-center gap-1"><EyeOff className="w-3 h-3" /> Done - hidden by default</li>
                                                <li>• Toggle at top of view to show Done</li>
                                                <li>• Grid adjusts to 3 or 4 columns</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-4 p-4 bg-slate-100 dark:bg-slate-800 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <Eye className="w-4 h-4 text-siemens-petrol" />
                                            <span className="text-xs font-bold">Visible</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <EyeOff className="w-4 h-4 text-slate-400" />
                                            <span className="text-xs font-bold text-slate-400">Hidden</span>
                                        </div>
                                    </div>
                                </div>
                            </Section>

                            {/* Section: WSJF Explained */}
                            <Section title="WSJF Scoring Explained" icon={Calculator}>
                                <div className="space-y-4">
                                    <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <span className="font-bold text-amber-800 dark:text-amber-200">Key Concept</span>
                                                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                                                    WSJF = <strong>Cost of Delay (CoD)</strong> ÷ <strong>Job Size (Effort)</strong>
                                                </p>
                                                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                                                    Higher WSJF scores indicate items that should be prioritized first.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <h4 className="font-bold text-slate-900 dark:text-white">The Formula</h4>
                                    <div className="p-4 bg-slate-900 dark:bg-black rounded-lg font-mono text-sm text-emerald-400 overflow-x-auto">
                                        <div className="whitespace-nowrap">
                                            CoD = (Security + Business + AdjustedTime + OpsEfficiency) × Confidence
                                        </div>
                                        <div className="whitespace-nowrap mt-2 text-cyan-400">
                                            WSJF = CoD ÷ Effort
                                        </div>
                                    </div>

                                    <h4 className="font-bold text-slate-900 dark:text-white pt-2">Lane Time Modifiers</h4>
                                    <p className="text-xs text-slate-500">
                                        Time Criticality is automatically adjusted based on the epic's lane:
                                    </p>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-center">
                                            <div className="font-bold text-emerald-700 dark:text-emerald-300">Now</div>
                                            <div className="text-lg font-black text-emerald-600">+2</div>
                                        </div>
                                        <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg text-center">
                                            <div className="font-bold text-amber-700 dark:text-amber-300">Next</div>
                                            <div className="text-lg font-black text-amber-600">+0</div>
                                        </div>
                                        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                                            <div className="font-bold text-blue-700 dark:text-blue-300">Later</div>
                                            <div className="text-lg font-black text-blue-600">-1</div>
                                        </div>
                                    </div>
                                </div>
                            </Section>

                            {/* Section: Scoring Dimensions */}
                            <Section title="Scoring Dimensions" icon={Scale}>
                                <p className="mb-4">Each epic is scored across these 6 dimensions (1-5 scale, except Effort):</p>
                                <div className="grid md:grid-cols-2 gap-3">
                                    <MetricCard
                                        icon={Shield}
                                        name="Security"
                                        description="Security impact, compliance requirements, vulnerability mitigation"
                                        range="1-5"
                                        color="bg-gradient-to-br from-red-500 to-rose-600"
                                    />
                                    <MetricCard
                                        icon={Briefcase}
                                        name="Business Value"
                                        description="Revenue potential, customer impact, strategic alignment"
                                        range="1-5"
                                        color="bg-gradient-to-br from-emerald-500 to-green-600"
                                    />
                                    <MetricCard
                                        icon={Clock}
                                        name="Time Criticality"
                                        description="Urgency, market timing, deadline pressure. Auto-adjusted by lane."
                                        range="1-5"
                                        color="bg-gradient-to-br from-amber-500 to-orange-600"
                                    />
                                    <MetricCard
                                        icon={Activity}
                                        name="Ops Efficiency"
                                        description="Operational improvements, cost savings, process optimization"
                                        range="1-5"
                                        color="bg-gradient-to-br from-blue-500 to-indigo-600"
                                    />
                                    <MetricCard
                                        icon={Check}
                                        name="Confidence"
                                        description="Certainty of estimates, requirements clarity. Multiplier for CoD."
                                        range="1-5"
                                        color="bg-gradient-to-br from-purple-500 to-violet-600"
                                    />
                                    <MetricCard
                                        icon={Zap}
                                        name="Job Size (Effort)"
                                        description="T-shirt sizing for effort. Lower = faster delivery = higher WSJF."
                                        range="1, 2, 3, 5"
                                        color="bg-gradient-to-br from-slate-600 to-slate-700"
                                    />
                                </div>
                            </Section>

                            {/* Section: User Roles */}
                            <Section title="User Roles & Permissions" icon={Users}>
                                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    <RoleCard
                                        role="Admin"
                                        color="bg-gradient-to-r from-purple-600 to-indigo-600"
                                        active={userRole === 'admin'}
                                        access={[
                                            "Full access to all features",
                                            "Manage users & roles",
                                            "Create/edit/delete projects",
                                            "Create/edit/delete epics",
                                            "Access audit logs"
                                        ]}
                                    />
                                    <RoleCard
                                        role="Project Manager"
                                        color="bg-gradient-to-r from-siemens-petrol to-siemens-deep-blue"
                                        active={userRole === 'pm'}
                                        access={[
                                            "Edit epics in assigned projects",
                                            "Change epic lanes & progress",
                                            "View all projects",
                                            "Access Kanban, Roadmap & Executive views"
                                        ]}
                                    />
                                    <RoleCard
                                        role="Epic Owner"
                                        color="bg-gradient-to-r from-emerald-600 to-green-600"
                                        active={userRole === 'owner'}
                                        access={[
                                            "Edit scoring on assigned epics",
                                            "Update progress & notes",
                                            "Cannot change lane or project",
                                            "Cannot reassign owners"
                                        ]}
                                    />
                                    <RoleCard
                                        role="Executive"
                                        color="bg-gradient-to-r from-amber-600 to-orange-600"
                                        active={userRole === 'executive'}
                                        access={[
                                            "Executive Dashboard access",
                                            "Team Performance metrics",
                                            "View portfolio health & progress",
                                            "Read-only on epics"
                                        ]}
                                    />
                                    <RoleCard
                                        role="Normal User"
                                        color="bg-gradient-to-r from-slate-500 to-slate-600"
                                        active={!userRole || userRole === 'normal'}
                                        access={[
                                            "View epics (read-only)",
                                            "Search & filter",
                                            "View Kanban board",
                                            "No edit permissions"
                                        ]}
                                    />
                                </div>
                            </Section>

                            {/* Section: Views */}
                            <Section title="Available Views" icon={BarChart3}>
                                <div className="space-y-3">
                                    {[
                                        { name: "Projects", desc: "Manage projects with progress bars showing completion %", icon: Folder },
                                        { name: "List View", desc: "Grouped view of epics by project with progress tracking", icon: BarChart3 },
                                        { name: "Kanban", desc: "Drag-and-drop epics between lanes. Toggle Unassigned/Done visibility", icon: Settings2 },
                                        { name: "Roadmap", desc: "Timeline view with toggle for Done lane. Shows prioritized work", icon: Target },
                                        { name: "Matrix", desc: "2D visualization comparing WSJF dimensions", icon: Scale },
                                        { name: "Dashboard", desc: "6 KPIs including Completed count, portfolio progress, recently completed", icon: TrendingUp },
                                        { name: "Executive", desc: "Strategic overview with Team Performance metrics, priority alerts, and project health", icon: Briefcase }
                                    ].map((view) => (
                                        <div key={view.name} className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <view.icon className="w-5 h-5 text-siemens-petrol" />
                                            <div>
                                                <div className="font-bold text-slate-900 dark:text-white text-sm">{view.name}</div>
                                                <div className="text-xs text-slate-500">{view.desc}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Section>

                            {/* Section: Team Performance */}
                            <Section title="Team Performance Metrics" icon={Activity}>
                                <div className="space-y-4">
                                    <p>The <strong>Team Performance</strong> section in Executive View shows owner workload and effectiveness:</p>

                                    <div className="grid md:grid-cols-3 gap-4">
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <Settings2 className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Lane Scope Toggle</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li>• <strong>Active Only</strong>: Now + Next lanes</li>
                                                <li>• <strong>All Assigned</strong>: + Later lanes</li>
                                                <li>• <strong>Later Only</strong>: Backlog analysis</li>
                                            </ul>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <BarChart3 className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Load Metrics</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li>• <strong>Active/Total/Later Load</strong>: Epic count</li>
                                                <li>• <strong>Effort</strong>: Sum of effort points</li>
                                                <li>• <strong>SGM %</strong>: Strategic alignment</li>
                                            </ul>
                                        </div>
                                        <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                                            <div className="flex items-center gap-2 mb-2">
                                                <TrendingUp className="w-5 h-5 text-siemens-petrol" />
                                                <span className="font-bold">Effectiveness</span>
                                            </div>
                                            <ul className="text-xs text-slate-500 space-y-1">
                                                <li>• <strong>Completed</strong>: Done epic count</li>
                                                <li>• <strong>Completion %</strong>: Done / Total</li>
                                                <li>• <strong>Value</strong>: Business value delivered</li>
                                                <li>• <strong>Efficiency</strong>: Value / Effort ratio</li>
                                            </ul>
                                        </div>
                                    </div>

                                    <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
                                        <div className="flex items-start gap-2">
                                            <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                                            <div>
                                                <span className="font-bold text-purple-800 dark:text-purple-200">Lane Scope Analysis</span>
                                                <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">
                                                    Use <strong>Later Only</strong> to see who has the largest backlog.
                                                    Use <strong>All Assigned</strong> for total workload distribution.
                                                    The column header updates dynamically based on your selection.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Section>

                            {/* Footer */}
                            <div className="pt-4 pb-8 text-center">
                                <p className="text-xs text-slate-400">
                                    ZPS Innovation • Powered by WSJF • Built for Zervice Projects
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}

export default HelpPage;
